/**
 * RTP Categories Management Page
 * Main component for managing RTP Categories with filtering and verification capabilities
 * Features:
 * - Category grid display with filtering capabilities
 * - Bulk selection and status management (Active/Inactive, Whitelist/Blacklist)
 * - Sync functionality for updating category data from VividSkybox API
 */
import React, { useState, useRef } from 'react';
import { Chip } from '@mui/material';
import RaList from '../../../components/ra-list/RaList';
import RenderControls from '../../../components/ra-list/RenderControls';
import { DatagridConfigurable, TextField, NumberField, FunctionField } from 'react-admin';
import { formatLocalTime } from '../../../components/ex_dayjs';
import VerificationActions from '../components/VerificationActions';
import { reloadPage } from '../../../utils/common';
import type {
    RTPCategory,
    FilterControl,
    FilterChoice,
    DefaultCategoryFilters
} from './types';

/**
 * Main component for RTP Categories management page
 * Provides interface for viewing, managing, and syncing event categories from VividSkybox API
 * @returns {JSX.Element} RTP Categories list component
 */
const RTP_Categories: React.FC = () => {
    const refbtnFilter = useRef<HTMLElement>(null);
    const [defaultSort] = useState<string[]>(['id asc']);
    const [defaultValues] = useState<DefaultCategoryFilters>({
        //verification_status_eq: 1
    });

    /**
     * Reloads the main page data by triggering filter refresh
     */
    const reloadMainPage = (): void => {
        reloadPage('RTP_Categories', refbtnFilter);
    };

    // Status options for filtering
    const statusOptions: FilterChoice[] = [
        { id: 0, name: 'InActive' },
        { id: 1, name: 'Active' }
    ];

    // Verification status options for filtering
    const verificationStatusOptions: FilterChoice[] = [
        { id: 0, name: 'Not Verified' },
        { id: 1, name: 'Whitelist' },
        { id: 2, name: 'Blacklist' },
    ];

    // Filter controls configuration
    const controls: FilterControl[] = [
        { component: 'text', name: 'name', label: 'Name', xs: 2 },
        { component: 'text', name: 'eventType', label: 'Event Type', xs: 2 },
        { component: 'select', name: 'status', label: 'Status', choices: statusOptions, xs: 1 },
        { component: 'select', name: 'verification_status_eq', label: 'Verification', choices: verificationStatusOptions, xs: 1 }
    ];

    /**
     * Renders status chip based on category status value
     * @param record - The category record containing status information
     * @returns {JSX.Element} Status chip component
     */
    const renderStatusChip = (record: RTPCategory): JSX.Element => (
        <Chip
            label={record.status === 1 ? 'Active' : 'InActive'}
            color={record.status === 1 ? 'success' : 'default'}
            size="small"
        />
    );

    /**
     * Gets verification configuration for status rendering
     * @param status - The verification status value
     * @returns {object} Configuration object with label and color
     */
    const getVerificationConfig = (status: number): { label: string; color: 'default' | 'success' | 'error' } => {
        switch (status) {
            case 0:
                return { label: 'Not Verified', color: 'default' };
            case 1:
                return { label: 'Whitelist', color: 'success' };
            case 2:
                return { label: 'Blacklist', color: 'error' };
            default:
            // Log unexpected verification status values for debugging
                console.warn(`Unexpected verification_status value: ${status}`);
                return { label: 'Unknown', color: 'default' };
        }
    };

    /**
     * Renders verification status chip based on verification status value
     * @param record - The category record containing verification status information
     * @returns {JSX.Element} Verification status chip component
     */
    const renderVerificationChip = (record: RTPCategory): JSX.Element => {
        const config = getVerificationConfig(record.verification_status);
        return (
            <Chip
                label={config.label}
                color={config.color}
                size="small"
            />
        );
    };

    /**
     * Renders formatted date string for created field
     * @param record - The category record containing created date information
     * @returns {string} Formatted date string or empty string
     */
    const renderCreatedDate = (record: RTPCategory): any =>
        record.created ? formatLocalTime(record.created) : '';

    /**
     * Renders formatted date string for modified field
     * @param record - The category record containing modified date information
     * @returns {string} Formatted date string or empty string
     */
    const renderModifiedDate = (record: RTPCategory): any =>
        record.modified ? formatLocalTime(record.modified) : '';

    return (
        <>
            <RaList
                sortby={defaultSort}
                perPage={25}
                storeKey={true}
                title={'Event Categories Management'}
                filters={
                    <RenderControls
                        controls={controls}
                        refFilter={refbtnFilter}
                        defaultValues={defaultValues}
                        // actions={<SyncButton reload={reloadMainPage} />}
                        exportFile={{
                            filename: `RTP-Categories-${formatLocalTime(new Date())}`,
                            columns: ['id', 'name', 'eventType', 'status', 'created', 'modified'],
                            limit: 1000000
                        }}
                    />
                }
            >
                <DatagridConfigurable
                    bulkActionButtons={<VerificationActions display={true} reload={reloadMainPage} action='Update' entityType="Categories" />}
                >
                    <NumberField source="id" label="ID" />
                    <TextField source="name" label="Name" />
                    <TextField source="eventType" label="Event Type" />
                    <FunctionField
                        source="status"
                        label="Status"
                        render={renderStatusChip}
                    />
                    <FunctionField
                        source="verification_status"
                        label="Verification"
                        render={renderVerificationChip}
                    />
                    <FunctionField
                        source="created"
                        label="Created"
                        render={renderCreatedDate}
                    />
                    <FunctionField
                        source="modified"
                        label="Modified"
                        render={renderModifiedDate}
                    />
                </DatagridConfigurable>
            </RaList>
        </>
    );
};

/**
 * RTP Categories Page configuration object
 * Follows the pattern expected by the React Admin resource system
 */
interface RtpCategoriesPageConfig {
    name: string;
    list: React.FC;
}

const RTP_CategoriesPage: RtpCategoriesPageConfig = {
    name: 'RTP_Categories',
    list: RTP_Categories
};

export default RTP_CategoriesPage;